import React, { useState, useEffect, useCallback } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import swal from "sweetalert";
import AddPaymentGatewayModal from "./addpaymentMethodModel";
import { appSettings } from "../../app/appSettingsSlice";

const ManagePaymentGateways = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const [loading, setLoading] = useState(false);
  const [paymentGateways, setPaymentGateways] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [gatewayToEdit, setGatewayToEdit] = useState(null);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  // Get country from Redux profile data
  const profileData = useSelector((state) => state.auth.profileData);
  const compCountry = profileData?.compCountry || "";

  // Check if payment methods are allowed for this country
  const isPaymentMethodsAllowed = compCountry?.toLowerCase() !== "pak";



  const fetchPaymentGateways = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_PAYMENT_GATEWAYS, {
        params: { CompID },
      });

      setPaymentGateways(response.data.data || []);
    } catch (err) {
      showErrorToast("Failed to load payment gateways");
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  useEffect(() => {
    fetchPaymentGateways();
  }, [fetchPaymentGateways]);

  const handleDeleteGateway = async (gateway) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${gateway.pgName}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) return;

    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_PAYMENT_GATEWAY, {
        params: {
          PG_ID: gateway.id,
          CompID,
        },
      });
      if (response.data.status) {
        setPaymentGateways((prev) => prev.filter((g) => g.id !== gateway.id));
        showSuccessToast("Payment gateway deleted successfully");
      }
    } catch (err) {
      showErrorToast("Failed to delete payment gateway");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentGatewaySubmit = (payload, isUpdate) => {
    if (isUpdate) {
      setPaymentGateways((prev) =>
        prev.map((gateway) =>
          gateway.id === payload.ID ? { ...gateway, ...payload } : gateway
        )
      );
    } else {
      setPaymentGateways((prev) => [...prev, { ...payload, id: payload.ID || Date.now() }]);
      fetchPaymentGateways();
    }
  };

  const handleEditGateway = (gateway) => {
    setGatewayToEdit({
      ID: gateway.id,
      PGName: gateway.pgName,
      PGHostURL: gateway.pgHostURL,
      PGAPIPriKey: gateway.pgapiPriKey,
      PGAPISecKey: gateway.pgapiSecKey,
      EnteryDateTime: gateway.enteryDateTime,
    });
    setIsModalOpen(true);
  };

  return (
    <div className="container-fluid px-4 py-4">
      {loading && <Preloader />}

      {/* Check if payment methods are allowed for this country */}
      {!isPaymentMethodsAllowed ? (
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="card border-0 shadow-lg" style={{ marginTop: '80px' }}>
              <div className="card-body text-center py-5">
                <div className="mb-4">
                  <i
                    className="fa fa-ban fa-5x"
                    style={{
                      color: '#dc3545',
                      opacity: 0.7
                    }}
                  ></i>
                </div>
                <h2 className="mb-3" style={{ fontWeight: 600 }}>
                  Feature Not Available
                </h2>
                <p className="text-muted mb-4" style={{ fontSize: '16px' }}>
                  Payment Methods feature is not available in your country.
                </p>
                <p className="text-muted mb-4" style={{ fontSize: '14px' }}>
                  This feature is currently restricted for your country. Please contact support if you have any questions.
                </p>
                <button
                  className="btn btn-primary px-4"
                  onClick={() => window.history.back()}
                  style={{
                    backgroundColor: themeColor,
                    border: `1px solid ${themeColor}`,
                    boxShadow: `0 4px 15px ${themeColor}40`,
                  }}
                >
                  <i className="fa fa-arrow-left me-2"></i>
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="d-flex align-items-center mb-3">
            <div>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="/">Home</a>
                </li>
                <li className="breadcrumb-item active">Manage Payment Gateways</li>
              </ol>
              <h1 className="page-header mb-0">Manage Payment Gateways</h1>
            </div>
            <div className="ms-auto">
              <button
                className="btn btn-switchery px-4"
                onClick={() => {
                  setGatewayToEdit(null);
                  setIsModalOpen(true);
                }}
                style={{
                  backgroundColor: themeColor,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  border: `1px solid ${themeColor}`,
                  boxShadow: `0 4px 15px ${themeColor}40`,
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = `0 6px 20px ${themeColor}60`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 4px 15px ${themeColor}40`;
                }}
              >
                <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
                Add Payment Gateway
              </button>
            </div>
          </div>

          <AddPaymentGatewayModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            onPaymentGatewaySubmit={handlePaymentGatewaySubmit}
            compID={CompID}
            paymentGatewayToEdit={gatewayToEdit}
            modalSize="lg"
            setPaymentGateways={setPaymentGateways}
          />

          <div className="card border-0">
            <div className="tab-content p-3">
              <div className="tab-pane fade show active">
                <div className="table-responsive" style={{ minHeight: "200px" }}>
                  <table className="table table-hover">
                    <thead className="bg-light">
                      <tr>
                        <th className="pt-2 pb-2">Name</th>
                        <th className="pt-2 pb-2">Host URL</th>
                        <th className="pt-2 pb-2">Entry Date</th>
                        <th className="pt-2 pb-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentGateways.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center">
                            No payment gateways available.
                          </td>
                        </tr>
                      ) : (
                        paymentGateways.map((gateway) => (
                          <tr key={gateway.id}>
                            <td className="align-middle">{gateway.pgName}</td>
                            <td className="align-middle">{gateway.pgHostURL}</td>
                            <td className="align-middle">{gateway.enteryDateTime}</td>
                            <td className="align-middle text-center">
                              <div className="d-flex justify-content-center gap-2">
                                {/* Edit Icon */}
                                <button
                                  onClick={() => handleEditGateway(gateway)}
                                  className="btn btn-sm btn-outline-primary"
                                  title="Edit Payment Gateway"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: `1px solid ${themeColor}`,
                                    color: themeColor,
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = themeColor;
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = themeColor;
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                </button>

                                {/* Delete Icon */}
                                <button
                                  onClick={() => handleDeleteGateway(gateway)}
                                  className="btn btn-sm btn-outline-danger"
                                  title="Delete Payment Gateway"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: '1px solid #dc3545',
                                    color: '#dc3545',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#dc3545';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#dc3545';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ManagePaymentGateways;